import {
  faBell,
  faCalendarPlus,
  faClock,
  faDollar,
  faEllipsisV,
  faEnvelope,
  faQuestion,
  faTablet,
  faUserPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Avatar } from 'src/components/Avatar';
import { Badge } from 'src/components/Badge';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { Dot } from 'src/components/Dot';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Link } from 'src/components/Link';
import { ListItem } from 'src/components/ListItem';
import { Scroller } from 'src/components/Scroller';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { Conversation } from 'src/models/conversation/_types';
import { fetchConversations } from 'src/models/conversation/conversation';
import {
  fetchNotifications,
  readNotification,
} from 'src/models/notification/notification';
import { isHQPlatform } from 'src/models/platform/utils';
import { isTrainer } from 'src/models/user/utils';
import { PaymentDialog } from 'src/pages/Clients/PaymentDialog/PaymentDialog';
import TimeClock from 'src/pages/TimeTracking/TimeClock';
import { useTimeClockPage } from 'src/pages/TimeTracking/useTimeClockPage';
import { UserDropdown } from 'src/shared/UserDropdown';
import { Timestamp } from 'src/types/utilityTypes';
import { formatListDateAndTime, formatTimeAgo } from 'src/utils/dateFormatter';

export interface ActionsMenuProps {
  size?: 'small' | 'medium';
  hideUserMenu?: boolean;
}

export const ActionsMenuDreamcoat: React.FC<ActionsMenuProps> = ({
  size = 'medium',
  hideUserMenu = false,
}) => {
  const { user, session } = useSession();
  const isHQ = isHQPlatform(session);
  if (!isTrainer(user)) {
    return (
      <Stack
        justifyContent="space-between"
        direction={'row'}
        gap={size === 'small' ? 0.5 : 1}
      >
        <MessagesMenu size={size} />
        <NotificationsMenu size={size} />
        <PendoMenu size={size} />
        {!hideUserMenu && <UserDropdown />}
      </Stack>
    );
  }

  return (
    <Stack
      justifyContent="space-between"
      direction={size === 'small' ? 'row-reverse' : 'row'}
      gap={size === 'small' ? 0.5 : 1}
    >
      {!isHQ && (
        <>
          <QuickActionsMenu size={size} />
        </>
      )}
      <NotificationsMenu size={size} />
      <PendoMenu size={size} />
      {!hideUserMenu && <UserDropdown />}
    </Stack>
  );
};

export const PendoMenu: React.FC<{ size: 'small' | 'medium' }> = ({
  size = 'medium',
}) => {
  return (
    <IconButton
      id="pendo-dropdown"
      size={size}
      variant={size === 'small' ? 'default' : 'tertiary'}
    >
      <Icon size="small" icon={faQuestion} color="neutral" />
    </IconButton>
  );
};

export const MessagesMenu: React.FC<{ size: 'small' | 'medium' }> = ({
  size = 'medium',
}) => {
  const { user } = useSession();
  const { t } = useTranslation('translation', {
    keyPrefix: 'actionsMenuDreamcoat',
  });

  const { data: unreadConversations } = useQuery({
    queryKey: ['unreadConversations'],
    queryFn: () =>
      fetchConversations({
        withUser: true,
        sortDir: 'desc',
        page: 1,
        perPage: 50,
        unreadOnly: true,
      }),
    staleTime: 1000 * 60,
  });
  const { data: allConversations } = useQuery({
    queryKey: ['conversations'],
    queryFn: () =>
      fetchConversations({
        withUser: true,
        sortDir: 'desc',
        page: 1,
        perPage: 50,
        unreadOnly: false,
      }),
    staleTime: 1000 * 60,
  });

  const conversationsWithDuplicates = [
    ...(unreadConversations?.conversations || []),
    ...(allConversations?.conversations || []),
  ];

  const uniqueConvoIds = new Set();
  const conversations = conversationsWithDuplicates
    .filter((item) => {
      const duplicate = uniqueConvoIds.has(item.conversationId);
      uniqueConvoIds.add(item.conversationId);
      return !duplicate;
    })
    .sort((a, b) => Number(b.lastUpdated) - Number(a.lastUpdated));

  const unreadCount = conversations.filter((c) => c.unread).length;

  const getOtherParticipants = (
    conversation: Conversation
  ): Conversation['participantDetails'] => {
    const participants = conversation.participantDetails.filter(
      (details) => details.id !== user.id
    );
    return participants.length > 0
      ? participants
      : conversation.participantDetails;
  };

  return (
    <DropdownMenu
      drawerOnMobile
      trigger={
        <Badge
          badgeContent={unreadCount}
          invisible={!unreadCount}
          color="error"
        >
          <IconButton
            size={size}
            variant={size === 'small' ? 'default' : 'tertiary'}
          >
            <Icon size="small" icon={faEnvelope} color="neutral" />
          </IconButton>
        </Badge>
      }
    >
      <Box
        sx={{
          minWidth: '320px',
          maxWidth: '600px',
        }}
      >
        <Typography variant="h5" sx={{ p: 2 }}>
          {t('messages')}
        </Typography>
        <Scroller direction="column" sx={{ maxHeight: '400px' }}>
          {conversations.length === 0 && (
            <Typography
              color="text.secondary"
              variant="body1"
              sx={{ px: 2, pb: 2 }}
            >
              {t('noRecentMessages')}
            </Typography>
          )}
          {conversations.map((conversation) => {
            const otherParticipants = getOtherParticipants(conversation);
            const participantName =
              otherParticipants[0].name || otherParticipants[0].email;
            const avatarSrc = otherParticipants[0].profileImage;
            return (
              <ListItem
                component={RouterLink}
                to={`/messages/${conversation.conversationId}`}
                primary={<>{participantName}</>}
                secondary={<>{formatTimeAgo(conversation.lastUpdated)}</>}
                avatar={<Avatar src={avatarSrc} />}
                rightAdornment={conversation.unread && <Dot />}
                clickable
                rounded
              />
            );
          })}
        </Scroller>
        <Stack direction="row-reverse" sx={{ p: 1 }}>
          <Button onClick={() => {}}>{t('goToInbox')}</Button>
        </Stack>
      </Box>
    </DropdownMenu>
  );
};

export const QuickActionsMenu: React.FC<{ size: 'small' | 'medium' }> = ({
  size,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'actionsMenuDreamcoat',
  });

  const [open, setOpen] = useState(false);
  const { user } = useSession();
  const { logEntry } = useTimeClockPage(user);
  const clockedIn = logEntry ? !logEntry?.endDate : false;

  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);

  const smDown = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  return (
    <>
      <DropdownMenu
        drawerOnMobile
        trigger={
          smDown ? (
            <IconButton size={size}>
              <Icon color="neutral" size="small" icon={faEllipsisV} />
            </IconButton>
          ) : (
            <Button size={size} variant="tertiary" sx={{ textWrap: 'nowrap' }}>
              {t('iWantTo')}
            </Button>
          )
        }
      >
        <Box sx={{ minWidth: 220 }}>
          <ListItem
            component={RouterLink}
            to={'/clients/new'}
            icon={faUserPlus}
            primary={t('newClient')}
            clickable
            rounded
          />
          <ListItem
            component={Link}
            href={externalLinks.v3Url('/booking')}
            target="_blank"
            icon={faCalendarPlus}
            primary={t('booking')}
            clickable
            rounded
          />
          <ListItem
            icon={faClock}
            primary={t('clockIn')}
            onClick={() => setOpen(true)}
            rightAdornment={
              clockedIn && (
                <Dot
                  sx={{
                    bgcolor: 'success.main',
                    width: '9px',
                    height: '9px',
                  }}
                />
              )
            }
            clickable
            rounded
          />
          <ListItem
            component={Link}
            href={externalLinks.accountManager()}
            target="_blank"
            icon={faTablet}
            primary={t('iPadAccountManager')}
            clickable
            rounded
          />
          <ListItem
            onClick={() => {
              setIsPaymentDialogOpen(true);
            }}
            icon={faDollar}
            primary={t('chargeAccount')}
            clickable
            rounded
          />
        </Box>
      </DropdownMenu>
      <Dialog title={t('timeClock')} open={open} onClose={() => setOpen(false)}>
        <TimeClock
          onSubmit={() => {}}
          user={user}
          clockedIn={clockedIn}
          logEntry={logEntry}
        />
      </Dialog>
      <PaymentDialog
        open={isPaymentDialogOpen}
        onClose={() => setIsPaymentDialogOpen(false)}
        user={undefined}
        onPaymentComplete={() => setIsPaymentDialogOpen(false)}
      />
    </>
  );
};

export const NotificationsMenu: React.FC<{ size: 'small' | 'medium' }> = ({
  size,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'actionsMenuDreamcoat',
  });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const queryClient = useQueryClient();
  const { data: notifications } = useQuery({
    queryKey: ['notifications'],

    queryFn: () => {
      return fetchNotifications();
    },

    refetchOnWindowFocus: false,
    gcTime: 10000,
  });

  const buttonType = size === 'medium' ? 'tertiary' : 'default';

  interface NotificationParams {
    start: Timestamp;
    end?: Timestamp;
  }

  const readNotificationMutation = useMutation({
    mutationFn: ({ start, end }: NotificationParams) => {
      return readNotification({ start, end });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return (
    <DropdownMenu
      drawerOnMobile
      trigger={
        <Badge
          invisible={!notifications?.length}
          badgeContent={notifications?.length}
          color="error"
        >
          <IconButton size={size} variant={buttonType}>
            <Icon color="neutral" size="small" icon={faBell} />
          </IconButton>
        </Badge>
      }
    >
      <Box
        sx={{
          minWidth: '320px',
          maxWidth: '600px',
        }}
      >
        <Stack direction="row" alignItems="center" px={2} py={2} pb={1}>
          <Typography variant="h5">{t('notifications')}</Typography>
        </Stack>

        <Box
          sx={{
            p: 1,
          }}
        >
          {!!notifications?.length &&
            notifications.map((notification) => (
              <ListItem
                clickable
                onClick={() => {
                  const link = notification.link.replace('wtcom://', '/');
                  window.open(link, '_blank');
                  readNotificationMutation.mutate({
                    start: notification.time,
                  });
                }}
                key={notification.string}
                avatar={<Avatar src={notification.userimage} />}
                secondary={formatListDateAndTime(notification.time)}
                centerIndent={false}
                rounded
                primary={
                  <div
                    dangerouslySetInnerHTML={{ __html: notification.string }}
                  />
                }
              />
            ))}

          {!notifications?.length && (
            <Typography
              color="text.secondary"
              variant="subtitle1"
              px={1}
              pt={1}
              pb={2}
            >
              {tCommon('messages.noUnreadNotifications')}
            </Typography>
          )}
        </Box>

        <Stack direction="row-reverse" pb={1} px={2} gap={2}>
          <Button
            size="small"
            onClick={() =>
              window.open(externalLinks.v2Url('/notifications'), '_blank')
            }
            disabled={!notifications?.length}
          >
            {tCommon('buttons.showAll')}
          </Button>
          <Button
            size="small"
            disabled={!notifications?.length}
            onClick={() => {
              if (notifications?.length) {
                readNotificationMutation.mutate({
                  end: notifications[0]?.time,
                  start: notifications[notifications.length - 1]?.time,
                });
              }
            }}
          >
            {tCommon('buttons.markAsRead')}
          </Button>
        </Stack>
      </Box>
    </DropdownMenu>
  );
};
